import {
  PropertyDto,
  PropertyMemberDto,
  PropertyMemberRole,
  PropertyMemberStatus,
  UserDto,
} from '@workspace/data-types';

/**
 * Checks if the given user is the principal owner of the property.
 */
export function isPropertyOwner(
  property: PropertyDto | null | undefined,
  user: UserDto | null | undefined,
): boolean {
  if (!property?.owner || !user?._id) return false;
  const rawOwner = property.owner as unknown;
  const ownerId =
    typeof rawOwner === 'object' && rawOwner !== null && '_id' in rawOwner
      ? (rawOwner as { _id?: string })._id?.toString()
      : (rawOwner as { toString?: () => string })?.toString();
  return Boolean(ownerId && user._id && ownerId === user._id.toString());
}

/**
 * Finds the accepted member record corresponding to the given user in property members.
 */
export function getPropertyAcceptedMember(
  property: PropertyDto | null | undefined,
  user: UserDto | null | undefined,
): PropertyMemberDto | undefined {
  if (!property?.members?.length || !user) return undefined;
  const userId = user._id?.toString();
  const userEmail = user.contactInfos?.email?.trim().toLowerCase();

  return property.members.find((m) => {
    if (m.status !== PropertyMemberStatus.ACCEPTED) return false;
    const memberUserId =
      typeof m.user === 'object' && m.user && '_id' in m.user
        ? (m.user as UserDto)._id?.toString()
        : m.user?.toString();
    const memberEmail = m.email?.trim().toLowerCase();

    return (
      (Boolean(userId) && Boolean(memberUserId) && memberUserId === userId) ||
      (Boolean(userEmail) && Boolean(memberEmail) && memberEmail === userEmail)
    );
  });
}

/**
 * Checks if the user is an accepted co-owner (role: CO_OWNER).
 */
export function isPropertyCoOwner(
  property: PropertyDto | null | undefined,
  user: UserDto | null | undefined,
): boolean {
  const member = getPropertyAcceptedMember(property, user);
  return member?.role === PropertyMemberRole.CO_OWNER;
}

/**
 * Checks if the user is an accepted spectator/viewer (role: VIEWER).
 */
export function isPropertyViewer(
  property: PropertyDto | null | undefined,
  user: UserDto | null | undefined,
): boolean {
  const member = getPropertyAcceptedMember(property, user);
  return member?.role === PropertyMemberRole.VIEWER;
}

/**
 * Checks if the user is authorized to edit the property data (Owner or Co-owner).
 * Spectators (VIEWER) are strictly read-only.
 */
export function canUserEditProperty(
  property: PropertyDto | null | undefined,
  user: UserDto | null | undefined,
): boolean {
  return isPropertyOwner(property, user) || isPropertyCoOwner(property, user);
}

/**
 * Checks if the user can invite new co-managers or resend invitations.
 * Only the principal owner can invite and manage members.
 */
export function canUserManagePropertyMembers(
  property: PropertyDto | null | undefined,
  user: UserDto | null | undefined,
): boolean {
  return isPropertyOwner(property, user);
}

/**
 * Checks if the user is authorized to delete the property (Principal owner only).
 * Co-managers (co-owners or viewers) are forbidden from deleting the property.
 */
export function canUserDeleteProperty(
  property: PropertyDto | null | undefined,
  user: UserDto | null | undefined,
): boolean {
  return isPropertyOwner(property, user);
}
